<template>
  <div v-if="visibleBets.length > 0 || snackbar">
    <div
      v-for="item in visibleBets"
      :key="item.key"
      class="biac-panel"
      :class="'biac-panel--' + item._type"
    >
      <!-- ── Header ───────────────────────────────────── -->
      <div class="biac-code-block" :class="'biac-code-block--' + item._type">
        <div class="biac-status-icon">
          <div v-if="item._type === 'pending'" class="biac-pending-dots">
            <div class="biac-pending-dot"></div>
            <div class="biac-pending-dot"></div>
            <div class="biac-pending-dot"></div>
          </div>
          <v-icon v-else-if="item._type === 'accepted'" size="15" color="#22c55e">mdi-checkbox-marked-circle</v-icon>
          <v-icon v-else-if="item._type === 'change'"   size="15" color="#f59e0b">mdi-swap-horizontal</v-icon>
          <v-icon v-else-if="item._type === 'rejected'" size="15" color="#ef4444">mdi-close-circle</v-icon>
        </div>

        <span class="biac-title text-uppercase">{{ statusLabel(item) }}</span>

        <!-- Auto-close countdown bar sits on the bottom edge of this block -->
        <div
          v-if="hasProgress(item.key)"
          class="biac-progress-bar"
          :style="progressStyle(item)"
        ></div>
      </div>
      <div class="biac-code-container" v-if="item._type !== 'rejected'">
        <span v-if="displayCode(item)" class="biac-code">{{ displayCode(item) }}</span>
        <button
            v-if="displayCode(item)"
            class="biac-copy-btn"
            :title="$t('copy')"
            @click="copyCode(displayCode(item))"
        >
          <v-icon size="12">mdi-content-copy</v-icon>
        </button>
      </div>

      <!-- ── Odds list (from snapshot) ────────────────── -->
      <div v-if="item.odds && item.odds.length > 0 && item._type !== 'rejected'" class="biac-odds-list">
        <template v-for="(group, gIdx) in getGroupedOdds(item)">

          <!-- BetBuilder group -->
          <div v-if="group.is_bet_builder" :key="'bb-' + gIdx" class="biac-odd-row">
            <div class="biac-odd-event">
              <BetBuilderBadge default-text="BB" class="conf-bb-badge" />
              <span class="biac-event-name">{{ group.odds[0].event_description }}</span>
            </div>
            <div class="conf-bb-timeline">
              <div
                v-for="(odd, idx) in group.odds"
                :key="odd.unique_id"
                class="conf-bb-row"
                :class="{ 'conf-bb-row--last': idx === group.odds.length - 1 }"
              >
                <div class="conf-bb-dot"></div>
                <div class="conf-bb-content">
                  <span class="conf-bb-outcome">{{ getOutcomeName(odd) }}</span>
                  <span class="conf-bb-market">{{ odd.market_alias || odd.market_name }}</span>
                </div>
              </div>
              <div class="conf-bb-combo-row">
                <span class="biac-odd-value conf-bb-combined-odd">{{ getBBCombinedOdd(group) | odds_type_filter }}</span>
              </div>
            </div>
          </div>

          <!-- Normal leg -->
          <div v-else :key="'leg-' + gIdx" class="biac-odd-row">
            <div class="biac-odd-event">
              <img
                v-if="group.odds[0].sport && group.odds[0].sport.id"
                :src="sportPath + group.odds[0].sport.id + '.svg'"
                width="12" height="12"
                class="biac-sport-icon"
              >
              <span class="biac-event-name">{{ group.odds[0].event_description }}</span>
              <span v-if="group.odds[0].type === 'live'" class="live-badge">LIVE</span>
            </div>
            <div class="biac-odd-detail">
              <span class="biac-market-name">
                {{ group.odds[0].market_alias || group.odds[0].market_name }}
                <span v-if="getOddLine(group.odds[0])" class="biac-specifier">{{ getOddLine(group.odds[0]) }}</span>
              </span>
              <div class="biac-odd-row-bottom">
                <span class="biac-selection">{{ getOutcomeName(group.odds[0]) }}</span>
                <!-- Riproposta admin: quota diversa da quella richiesta dal player -->
                <span
                  v-if="item._type === 'change' && hasOddChanged(group.odds[0])"
                  class="biac-odd-change"
                >
                  <v-icon x-small color="warning" class="biac-odd-change-icon">mdi-swap-vertical</v-icon>
                  <span class="biac-old-odd">{{ parseFloat(group.odds[0].outcome_odd_value_for_order) | odds_type_filter }}</span>
                  <v-icon x-small class="biac-odd-change-arrow">mdi-arrow-right</v-icon>
                  <span class="biac-new-odd">{{ parseFloat(group.odds[0].outcome_odd_value) | odds_type_filter }}</span>
                </span>
                <span v-else class="biac-odd-value">{{ group.odds[0].outcome_odd_value | odds_type_filter }}</span>
              </div>
            </div>
          </div>

        </template>
      </div>

      <!-- ── Summary card ──────────────────────────────── -->
      <div class="biac-summary-card" v-if="item._type !== 'rejected'">

        <!-- change: show proposed new amounts -->
        <template v-if="item._type === 'change'">
          <div class="biac-summary-row">
            <span class="biac-summary-label">{{ $t('amount') }}</span>
            <span class="biac-summary-value">{{ getSettings.currencySymbol }} {{ item.amount_real | round_digits(2) }}</span>
          </div>
          <div class="biac-summary-row biac-summary-row--winning">
            <span class="biac-summary-label">{{ $t('potentialWinnings') }}</span>
            <span class="biac-summary-value biac-summary-value--highlight">{{ getSettings.currencySymbol }} {{ item.winning_total | round_digits(2) }}</span>
          </div>
        </template>

        <!-- pending / accepted / rejected: show snapshot amounts -->
        <template v-else-if="item.amount != null">
          <div v-if="item.odds_total > 0" class="biac-summary-row">
            <span class="biac-summary-label">{{ $t(item.type) }}</span>
            <span class="biac-summary-value">{{ item.odds_total | round_digits(2) }}</span>
          </div>
          <div class="biac-summary-row">
            <span class="biac-summary-label">{{ $t('amount') }}</span>
            <span class="biac-summary-value">{{ getSettings.currencySymbol }} {{ item.amount | round_digits(2) }}</span>
          </div>
          <div v-if="item.bonus_percentage > 0" class="biac-summary-row">
            <span class="biac-summary-label">{{ $t('bonus') }}</span>
            <span class="biac-summary-value biac-summary-value--green">% {{ item.bonus_percentage }}</span>
          </div>
          <div class="biac-summary-row biac-summary-row--winning">
            <span class="biac-summary-label">{{ $t('winning') }} {{ $t('total') }}</span>
            <span class="biac-summary-value biac-summary-value--highlight">{{ getSettings.currencySymbol }} {{ item.winning_total | round_digits(2) }}</span>
          </div>
        </template>

      </div>

      <!-- ── Actions ───────────────────────────────────── -->
      <div v-if="item._type !== 'pending'" class="biac-actions">


        <!-- change -->
        <template v-if="item._type === 'change'">
          <div
            class="biac-action-btn biac-btn--accept"
            :class="{ 'biac-btn--loading': loadingActions[item.code] }"
            @click="acceptBetReview(item.code)"
          >
            <v-progress-circular
              v-if="loadingActions[item.code]"
              indeterminate size="13" width="2" color="#22c55e"
            ></v-progress-circular>
            <template v-else>
              <v-icon size="12">mdi-thumb-up</v-icon>
              <span>{{ $t('accept') }}</span>
            </template>
          </div>
          <div
            class="biac-action-btn biac-btn--reject"
            :class="{ 'biac-btn--loading': loadingActions[item.code] }"
            @click="rejectBetReview(item.code)"
          >
            <v-progress-circular
              v-if="loadingActions[item.code]"
              indeterminate size="13" width="2" color="#ef4444"
            ></v-progress-circular>
            <template v-else>
              <v-icon size="12">mdi-thumb-down</v-icon>
              <span>{{ $t('reject') }}</span>
            </template>
          </div>
        </template>


      </div>

    </div>

    <v-snackbar v-model="snackbar" :color="snackbarColor" :timeout="snackbarColor ? 4000 : 2500" top>{{ snackbarText }}</v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import BetBuilderBadge from '@/components/badges/BetBuilderBadge.vue';

export default {
  name: 'BetInAcceptanceInCart',
  components: { BetBuilderBadge },

  data: () => ({
    AUTO_CLOSE_SEC:   6,
    autoCloseTimers:  {},
    progressKeys:     new Set(),
    snackbar:         false,
    snackbarText:     '',
    snackbarColor:    '',
    sportPath:        process.env.VUE_APP_URL_CLOUDFRONT + '/svg/sports/icons-color/',
    loadingActions:   {}, // { [betCode_accept|betCode_reject]: true }
  }),

  computed: {
    ...mapGetters({
      getAcceptingBets:         'cart/getAcceptingBets',
      getAcceptedBets:          'cart/getAcceptedBets',
      getAcceptedBetWithChange: 'cart/getAcceptedBetWithChange',
      getRejectedBets:          'cart/getRejectedBets',
      getShortCode:             'settings/getShortCode',
      getSettings:              'cart/getSettings',
      getPlatformSettings:      'settings/getPlatformSettings',
      getOddsType:              'odds/getOddsType',
      getType:                  'user/getType',
      getId:                    'user/getId',
    }),

    allBets() {
      const all = [];
      (this.getAcceptingBets        || []).forEach(b => all.push({ ...b, _type: 'pending',  key: 'p_' + b.code }));
      (this.getAcceptedBets         || []).forEach(b => all.push({ ...b, _type: 'accepted', key: 'a_' + b.code }));
      (this.getAcceptedBetWithChange|| []).forEach(b => all.push({ ...b, _type: 'change',   key: 'c_' + b.code }));
      (this.getRejectedBets         || []).forEach(b => all.push({ ...b, _type: 'rejected', key: 'r_' + b.code }));
      return all;
    },

    // accepted bets are handled by BetConfirmedMarketView — don't render them here
    visibleBets() {
      return this.allBets.filter(b => b._type !== 'accepted');
    },
  },

  watch: {
    // immediate: anche le bet già nello store al mount (es. arrivate a componente
    // smontato o ripristinate da localStorage) devono avere il timer di chiusura
    allBets: {
      immediate: true,
      handler(val, old) {
        const autoTypes = ['accepted', 'rejected'];
        const oldKeys   = new Set((old || []).map(b => b.key));

        val.forEach(item => {
          if (!autoTypes.includes(item._type))       return;
          if (oldKeys.has(item.key))                 return;
          if (this.autoCloseTimers[item.key])        return;

          this.progressKeys = new Set([...this.progressKeys, item.key]);

          this.autoCloseTimers[item.key] = setTimeout(() => {
            this._autoRemove(item);
            delete this.autoCloseTimers[item.key];
            const next = new Set(this.progressKeys);
            next.delete(item.key);
            this.progressKeys = next;
          }, this.AUTO_CLOSE_SEC * 1000);
        });

        // Cancel timers for manually removed items
        const currentKeys = new Set(val.map(b => b.key));
        Object.keys(this.autoCloseTimers).forEach(k => {
          if (!currentKeys.has(k)) {
            clearTimeout(this.autoCloseTimers[k]);
            delete this.autoCloseTimers[k];
          }
        });
      },
    },
  },

  beforeDestroy() {
    Object.values(this.autoCloseTimers).forEach(id => clearTimeout(id));
  },

  methods: {
    ...mapActions({
      removeBetFromAcceptedBets:       'cart/removeBetFromAcceptedBets',
      removeBetFromRejectedBets:       'cart/removeBetFromRejectedBets',
      removeBetFromAcceptedWithChange: 'cart/removeBetFromAcceptedWithChange',
      acceptsRevisedBet:               'cart/acceptsRevisedBet',
      rejectRevisedBet:                'cart/rejectRevisedBet',
      resetCart:                       'cart/resetCart',
      setConfirmedBet:                 'cart/setConfirmedBet',
    }),

    // ── State helpers ─────────────────────────────────────
    hasProgress(key) { return this.progressKeys.has(key); },

    progressStyle(item) {
      const color = item._type === 'accepted' ? '#22c55e' : '#ef4444';
      return { '--progress-color': color, '--progress-dur': this.AUTO_CLOSE_SEC + 's' };
    },

    _autoRemove(item) {
      if (item._type === 'accepted') this.removeBetFromAcceptedBets(item.code);
      else if (item._type === 'rejected') this.removeBetFromRejectedBets(item.code);
    },

    statusLabel(item) {
      const type = item && item._type ? item._type : item;
      if (type === 'change') {
        return this.$t(this.changeLabelKey(item));
      }
      const map = {
        pending:  this.$t('acceptanceBet'),
        accepted: this.$t('betAccepted'),
        rejected: this.$t('betWasNotAccepted'),
      };
      return map[type] || type;
    },
    // True quando l'importo proposto dall'admin (amount_real) differisce
    // dall'importo originale del player (amount dallo snapshot).
    hasAmountChanged(item) {
      if (!item || item.amount == null || item.amount_real == null) return false;
      const original = parseFloat(item.amount);
      const proposed = parseFloat(item.amount_real);
      if (isNaN(original) || isNaN(proposed)) return false;
      return Math.abs(original - proposed) > 0.001;
    },
    changedOdds(item) {
      if (!item || !Array.isArray(item.odds)) return [];
      return item.odds.filter(this.hasOddChanged);
    },
    // Ritorna la chiave i18n corretta in base a cosa è cambiato nella riproposta.
    changeLabelKey(item) {
      const amt = this.hasAmountChanged(item);
      const odd = this.changedOdds(item).length > 0;
      if (amt && odd) return 'proposalChangeAmountAndOdd';
      if (odd)       return 'proposalChangeOdd';
      return 'proposalChangeAmount';
    },

    displayCode(bet) {
      return this.getShortCode ? (bet.betCode || bet.code) : bet.code;
    },

    // ── Odds helpers (mirrors BetConfirmedMarketView) ─────
    getGroupedOdds(item) {
      const odds = item.odds || [];
      const map  = new Map();
      for (const odd of odds) {
        const key = odd.bet_builder_id || odd.unique_id;
        if (!map.has(key)) map.set(key, { key, is_bet_builder: !!odd.bet_builder_id, odds: [] });
        map.get(key).odds.push(odd);
      }
      return Array.from(map.values());
    },

    getBBCombinedOdd(group) {
      const main = group.odds.find(o => parseFloat(o.outcome_odd_value) !== 1);
      return main ? main.outcome_odd_value : (group.odds[0] ? group.odds[0].outcome_odd_value : 0);
    },

    getOddLine(odd) {
      if (odd.line && odd.line !== '')     return odd.line;
      if (odd.baseLine && odd.baseLine !== '') return odd.baseLine;
      if (odd.specifiers && odd.specifiers !== '') {
        const part = odd.specifiers.split(' ')[0];
        return isNaN(part.charAt(0)) && part.charAt(0) !== '-' && part.charAt(0) !== '+' ? null : part;
      }
      return null;
    },

    // True when admin's proposed odd (outcome_odd_value) differs from player's
    // original request (outcome_odd_value_for_order). Used to render old→new.
    hasOddChanged(odd) {
      if (!odd || odd.outcome_odd_value_for_order == null) return false;
      const requested = parseFloat(odd.outcome_odd_value_for_order);
      const proposed = parseFloat(odd.outcome_odd_value);
      if (isNaN(requested) || isNaN(proposed)) return false;
      return Math.abs(requested - proposed) > 0.001;
    },

    getOutcomeName(odd) {
      const name = odd.outcome_name;
      if (!name) return odd.outcome_alias || '';
      if (name === 'X') return odd.outcome_alias || odd.outcome_name;
      const lower = name.toLowerCase();
      const c = odd.competitors;
      if (lower === 'local'    && c && c[0]) return c[0].name;
      if (lower === 'visitante'&& c && c[1]) return c[1].name;
      if ((name === '1' || name === '2') && odd.unique_id !== '128' && c) {
        const found = c.find(t => t.position === name);
        if (found) return found.name;
      }
      return odd.outcome_alias || name;
    },

    // ── Actions ───────────────────────────────────────────
    onPrint(item) {
      const code = item.code;
      if (!code) return;
      const base = this.getPlatformSettings.print_endpoint + code;
      const url  = this.getShortCode
        ? `${base}&login=${this.getType}&ancestors=${this.getId}&type_odd=${this.getOddsType}`
        : `${base}&type_odd=${this.getOddsType}`;
      window.open(url, '_blank', 'height=600,width=350');
    },

    onNewBet(item) {
      this.removeBetFromAcceptedBets(item.code);
      this.$root.$emit('cart_handling', true);
      this.resetCart();
    },

    onReloadBet(item) {
      this.removeBetFromAcceptedBets(item.code);
    },

    onClose(item) {
      this.removeBetFromRejectedBets(item.code);
    },

    async acceptBetReview(betCode) {
      if (this.loadingActions[betCode]) return;
      this.$set(this.loadingActions, betCode, true);

      try {
        const item = this.allBets.find(b => b.code === betCode && b._type === 'change');
        const res = await this.acceptsRevisedBet({ code: betCode });
        if (!this._handleReviewResult(res, betCode)) return;

        if (item) {
          await this.setConfirmedBet({
            bet_code:         item.code,
            is_booked:        false,
            type:             item.type   || 'single',
            amount:           item.amount_real != null ? item.amount_real : (item.amount || 0),
            odds_total:       item.odds_total  || 0,
            winning:          item.winning     || 0,
            winning_total:    item.winning_total || 0,
            bonus:            item.bonus            || 0,
            bonus_percentage: item.bonus_percentage  || 0,
            odds:             item.odds || [],
          });
        }
      } finally {
        this.$set(this.loadingActions, betCode, false);
      }
    },

    async rejectBetReview(betCode) {
      if (this.loadingActions[betCode]) return;
      this.$set(this.loadingActions, betCode, true);

      try {
        const res = await this.rejectRevisedBet({ code: betCode });
        if (this._handleReviewResult(res, betCode)) {
          this._showSnackbar(this.$t('betRevivedNotAccepted'));
        }
      } finally {
        this.$set(this.loadingActions, betCode, false);
      }
    },

    // Ritorna true se l'API ha confermato l'operazione. La riproposta viene sempre
    // rimossa dallo store (e marcata come risolta), anche se l'API va in errore.
    _handleReviewResult(res, betCode) {
      const data = (res && res.data) || {};
      this.removeBetFromAcceptedWithChange(betCode);
      if (data.result) return true;

      if (data.error_code === 'BET_NOT_IN_REVIEW') {
        this._showSnackbar(this.$t('betNotInReview'), 'error');
      } else {
        this._showSnackbar(this.$t('betReviewActionError'), 'error');
      }
      return false;
    },

    _showSnackbar(text, color = '') {
      this.snackbarText  = text;
      this.snackbarColor = color;
      this.snackbar      = true;
    },

    async copyCode(text) {
      if (!text) return;
      try {
        if (navigator.clipboard) await navigator.clipboard.writeText(text);
        else this._fallbackCopy(text);
      } catch { this._fallbackCopy(text); }
      this._showSnackbar(this.$t('codeCopiedToClipboard'));
    },

    _fallbackCopy(text) {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
      document.body.appendChild(ta);
      ta.focus(); ta.select();
      try { document.execCommand('copy'); } catch {}
      document.body.removeChild(ta);
    },
  },
};
</script>

<style scoped>
/* ── Panel wrapper ────────────────────────────────────── */
.biac-panel {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 0 8px;
  animation: biacFadeIn 0.3s ease;
  position: relative;
}

@keyframes biacFadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
}

/* ── Header / code block ──────────────────────────────── */
.biac-code-block {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: 8px;
  border: 1px solid var(--color-primary-light-op-50);
  position: relative;
  overflow: hidden;
}

.biac-code-block--pending  { background: rgba(245, 158, 11, 0.08); border-color: rgba(245, 158, 11, 0.3); }
.biac-code-block--accepted { background: var(--color-primary-gradient); border-color: var(--color-primary-light-op-50); }
.biac-code-block--change   { background: rgba(245, 158, 11, 0.08); border-color: rgba(245, 158, 11, 0.3); }
.biac-code-block--rejected { background: rgba(239, 68, 68, 0.08);  border-color: rgba(239, 68, 68, 0.3); }

.biac-status-icon {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.biac-title {
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--text-platform);
  flex: 1;
  letter-spacing: 0.04em;
}
.biac-code-container{
  display: flex;
  justify-content: flex-start;
  align-items: center;
  padding: 2px 12px;
  gap: 6px;
}

.biac-code {
  font-size: var(--font-size-2xl);
  font-weight: 800;
  color: var(--text-platform);
}

.biac-copy-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px 4px;
  opacity: 0.5;
  color: var(--text-platform);
  display: flex;
  align-items: center;
}
.biac-copy-btn:hover { opacity: 1; }
.biac-copy-btn .v-icon { color: var(--text-platform); }

/* ── Pending animated dots ───────────────────────────── */
.biac-pending-dots {
  display: flex;
  align-items: center;
  gap: 3px;
}
.biac-pending-dot {
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #f59e0b;
  animation: biac-dot-pulse 1.2s ease-in-out infinite;
}
.biac-pending-dot:nth-child(2) { animation-delay: 0.2s; }
.biac-pending-dot:nth-child(3) { animation-delay: 0.4s; }
@keyframes biac-dot-pulse {
  0%, 80%, 100% { opacity: 0.25; transform: scale(0.8); }
  40%           { opacity: 1;    transform: scale(1.15); }
}

/* ── Odds list ────────────────────────────────────────── */
.biac-odds-list { display: flex; flex-direction: column; gap: 4px; }

.biac-odd-row {
  background: var(--bg-card);
  border-radius: 6px;
  padding: 8px 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.biac-odd-event { display: flex; align-items: center; gap: 6px; }
.biac-sport-icon { flex-shrink: 0; opacity: 0.75; }
.biac-event-name {
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--text-platform);
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.live-badge {
  font-size: var(--font-size-xs);
  font-weight: 700;
  background: #ef4444;
  color: #fff;
  padding: 1px 4px;
  border-radius: 3px;
  flex-shrink: 0;
}

.biac-odd-detail {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.biac-market-name {
  font-size: var(--font-size-md);
  color: var(--text-op-50-platform);
  display: flex;
  align-items: center;
  gap: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.biac-specifier { color: var(--text-platform); font-weight: 700; flex-shrink: 0; }

.biac-odd-row-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}

.biac-selection {
  font-size: var(--font-size-xl);
  font-weight: 600;
  color: var(--text-platform);
}

.biac-odd-value {
  font-size: var(--font-size-xl);
  font-weight: 700;
  color: var(--color-primary, #22c55e);
  background: var(--color-primary-op);
  border-radius: 4px;
  padding: 2px 6px;
}

/* ── Riproposta: old→new odd change ─────────────────────── */
.biac-odd-change {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: var(--font-size-xl);
  font-weight: 700;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: 4px;
  padding: 2px 6px;
}
.biac-odd-change-icon { margin-right: 2px; }
.biac-odd-change-arrow { color: var(--text-op-50-platform) !important; margin: 0 1px; }
.biac-old-odd {
  color: var(--text-op-50-platform);
  text-decoration: line-through;
}
.biac-new-odd {
  color: var(--warning, #f59e0b);
  font-weight: 800;
}

/* ── BetBuilder in panel ──────────────────────────────── */
.conf-bb-badge {
  flex-shrink: 0;
  padding: 2px 5px;
  border-radius: 4px;
  font-size: var(--font-size-xs);
  font-weight: 800;
  line-height: 13px;
  color: var(--bg-body);
  background: var(--color-primary-light);
}
.conf-bb-timeline { display: flex; flex-direction: column; gap: 8px; padding: 6px 0 4px 16px; position: relative; margin-top: 4px; }
.conf-bb-row { position: relative; display: flex; flex-direction: column; gap: 1px; }
.conf-bb-row:not(.conf-bb-row--last)::before { content: ''; position: absolute; left: -9px; top: 5px; bottom: -8px; width: 1px; background: var(--color-primary-light, #21a8f7); opacity: 0.4; }
.conf-bb-dot { position: absolute; left: -12px; top: 4px; width: 6px; height: 6px; border-radius: 50%; background: var(--color-primary-light, #21a8f7); box-shadow: 0 0 4px rgba(33,168,247,0.6); z-index: 1; }
.conf-bb-content { display: flex; flex-direction: column; gap: 1px; }
.conf-bb-outcome { font-size: var(--font-size-xl); font-weight: 600; color: var(--text-platform); }
.conf-bb-market  { font-size: var(--font-size-md); color: var(--text-op-50-platform); text-transform: uppercase; }
.conf-bb-combo-row { display: flex; justify-content: flex-end; margin-top: 4px; padding-top: 6px; border-top: 1px solid rgba(255,255,255,0.07); }
.conf-bb-combined-odd { color: var(--color-primary-light) !important; background: var(--color-primary-op) !important; }

/* ── Summary card ─────────────────────────────────────── */
.biac-summary-card { background: var(--bg-card); border-radius: 6px; overflow: hidden; }
.biac-summary-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  font-size: var(--font-size-2xl);
  min-height: 30px;
}
.biac-summary-row + .biac-summary-row { border-top: 1px solid rgba(255, 255, 255, 0.05); }
.biac-summary-label  { color: var(--text-op-50-platform); font-weight: 500; }
.biac-summary-value  { font-weight: 600; color: var(--text-platform); }
.biac-summary-value--green { color: #22c55e; }
.biac-summary-row--winning {
  background: rgba(255, 255, 255, 0.03);
  border-top: 1px solid rgba(255, 255, 255, 0.08) !important;
}
.biac-summary-row--winning .biac-summary-label { color: var(--text-platform); font-weight: 600; }
.biac-summary-value--highlight { font-size: var(--font-size-3xl); font-weight: 700; color: var(--color-primary, #22c55e); }

/* ── Actions row ──────────────────────────────────────── */
.biac-actions {
  display: flex;
  gap: 6px;
  padding-top: 4px;
  flex-wrap: nowrap;
}

.biac-action-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 38px;
  border-radius: 6px;
  font-size: var(--font-size-xl);
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  cursor: pointer;
  user-select: none;
  transition: all 0.15s;
}

.icon-only-btn {
  flex: 0 0 38px;
  width: 38px;
  padding: 0;
}
.icon-only-btn .v-icon { margin: 0 !important; }

.cta-primary { flex: 1; min-width: 120px; }
.cta-primary span { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

.biac-action-btn--secondary {
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: var(--text-platform);
}
.biac-action-btn--secondary:hover {
  background: rgba(255, 255, 255, 0.07);
  border-color: rgba(255, 255, 255, 0.35);
}

.biac-action-btn--primary {
  background: var(--btn-background);
  border: none;
  color: var(--btn-text);
}
.biac-action-btn--primary .v-icon { color: var(--btn-text) !important; }
.biac-action-btn--primary:hover { transform: translateY(-1px); }

.biac-btn--accept {
  flex: 1;
  gap: 6px;
  background: rgba(34, 197, 94, 0.15);
  border: 1px solid rgba(34, 197, 94, 0.35);
  color: #22c55e;
}
.biac-btn--accept:hover { background: rgba(34, 197, 94, 0.28); }
.biac-btn--accept.biac-btn--loading { pointer-events: none; opacity: 0.65; }

.biac-btn--reject {
  flex: 1;
  gap: 6px;
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.3);
  color: #ef4444;
}
.biac-btn--reject:hover { background: rgba(239, 68, 68, 0.25); }
.biac-btn--reject.biac-btn--loading { pointer-events: none; opacity: 0.65; }

/* ── Auto-close progress bar ──────────────────────────── */
.biac-progress-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 3px;
  width: 100%;
  background: var(--progress-color, #ef4444);
  transform-origin: left center;
  animation: biac-countdown var(--progress-dur, 6s) linear forwards;
}
@keyframes biac-countdown {
  from { transform: scaleX(1); }
  to   { transform: scaleX(0); }
}
</style>
